/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { TextField, PasswordField, InputLabel } from 'components/elements';
import { fetchDefaultBranding } from 'store/actions/branding';
import { useAuthActions, useReactHookFormErrorMap } from 'hooks';
import { patterns } from 'const';
import { sessionProvider } from 'utils';

import Styles from './styles';

const useStyles = makeStyles(Styles);

function LoginForm({ onSubmit, children }) {
  const classes = useStyles();
  const userActions = useAuthActions();
  const dispatch = useDispatch();
  const { t } = useTranslation('labels');
  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    getValues,
    formState: { errors },
    watch,
  } = useForm({
    mode: 'onSubmit',
    defaultValues: { email: '', password: '' },
    reValidateMode: 'onSubmit',
  });
  const mapErrorToForm = useReactHookFormErrorMap(getValues, setError, '');
  useEffect(() => {
    const subscription = watch(() => clearErrors('apiError'));
    return () => subscription.unsubscribe();
  }, [watch]);
  const [showProviderPortalUrl, setShowProviderPortalUrl] = useState(false);
  const providerPortalURL = `${window.location.origin}/login`;

  const onSubmitForm = ({ email, password }) =>
    userActions
      .login(email, password)
      .then((isProvider) => {
        if (isProvider) {
          setShowProviderPortalUrl(true);
        } else {
          dispatch(fetchDefaultBranding());
          onSubmit();
        }
      })
      .catch((error) => mapErrorToForm(error));

  return (
    <form className={classes.form} onSubmit={handleSubmit(onSubmitForm)}>
      {children}
      <div className={classes.input}>
        <Controller
          className={classes.control}
          name="email"
          control={control}
          rules={{
            required: t('please_fill_in_this_field'),
            pattern: {
              value: patterns.email,
              message: t('email_invalid'),
            },
          }}
          render={({ field: { ref, ...restField }, fieldState: { error } }) => (
            <TextField error={!!error} helperText={error ? error.message : null} {...restField} label={<InputLabel text={t('email')} required />} />
          )}
        />
      </div>
      <div className={classes.input}>
        <Controller
          className={classes.control}
          name="password"
          control={control}
          rules={{ required: t('please_fill_in_this_field') }}
          render={({ field: { ref, ...restField }, fieldState: { error } }) => (
            <PasswordField error={!!error} helperText={error ? error.message : null} {...restField} label={<InputLabel text={t('password')} required />} />
          )}
        />
      </div>
      {errors.apiError && <div className={classes.error}>{errors.apiError.message}</div>}
      {showProviderPortalUrl && (
        <div className={classes.error}>
          {t('please_login_into_provider_portal')}
          <a href={providerPortalURL} onClick={sessionProvider.goToProviderPortal}>
            {providerPortalURL}
          </a>
        </div>
      )}
      <Button type="submit" variant="contained" color="primary" className={classes.submit}>
        {t('login')}
      </Button>
    </form>
  );
}

LoginForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  children: PropTypes.element,
};

LoginForm.defaultProps = {
  children: null,
};

export default LoginForm;
